// src/components/PromoModal.jsx
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";

export default function PromoModal({
  open,
  title,
  description,
  imageUrl,
  onClose,
  primaryLabel = "Got it",
  onPrimary,
  loading = false,
}) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-2 py-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={modalRef}
        className="relative bg-[#191930] w-[980px] max-w-[98vw] h-[86vh] rounded-2xl shadow-2xl flex flex-col min-h-[520px] overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="Close modal"
          disabled={loading}
          type="button"
        >
          <X size={22} />
        </button>

        <div className="px-8 pt-7 pb-3 border-b border-[#2B2B4A]">
          <div className="text-white font-semibold text-xl">{title || ""}</div>
          {description ? (
            <div className="text-gray-300 mt-2 leading-relaxed">
              {description}
            </div>
          ) : null}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6">
          {imageUrl ? (
            <div className="w-full h-full">
              <div className="w-full h-full rounded-xl border border-[#2B2B4A] bg-black/30 overflow-hidden flex items-center justify-center">
                <img
                  src={imageUrl}
                  alt="Promo"
                  className="w-full h-full object-contain"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            </div>
          ) : (
            <div className="text-gray-400">
              No promo image uploaded yet.
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-[#2B2B4A] bg-[#191930] px-8 py-4 flex items-center justify-end">
          <button
            type="button"
            onClick={onPrimary || onClose}
            disabled={loading}
            className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

PromoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  onClose: PropTypes.func,
  primaryLabel: PropTypes.string,
  onPrimary: PropTypes.func,
  loading: PropTypes.bool,
};
